import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import {
  useAssignPermission,
  useGetPermissions,
  useGetStaffPermissions,
  useRemovePermission,
} from "features/patients/hooks/usePermissions";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ModalField } from "./ModalField";
import styles from "./UserFormModal.module.css";
import {
  GENDER_OPTIONS,
  ROLE_OPTIONS,
  STAFF_ROLE_OPTIONS,
  TITLE_OPTIONS,
  toFormValues,
  userFormSchema,
} from "./userFormSchema";
import { UserPhotoField } from "./UserPhotoField";

const ACCESS_TOGGLES = [
  { name: "systemAccess", label: "Provide system access" },
  { name: "showOnTeam", label: "Show on team profile page" },
  { name: "roster", label: "Show on appointments page" },
];

/**
 * Add or edit a staff member.
 *
 * Saves three things together: the staff record, the access flags stored on
 * it, and the tenant's permission list — a separate resource, reconciled
 * against the server's current rows once the member has an id.
 *
 * Laid out to fit a desktop viewport without scrolling: three columns of
 * fields beside the photo, and the permissions in a grid rather than a list.
 */
export const UserFormModal = ({ isOpen, onClose, staffId, staff, onSave }) => {
  const [profilePic, setProfilePic] = useState(null);
  const [activePermissionIds, setActivePermissionIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();
  const { data: allPermissions = [] } = useGetPermissions();
  const { data: staffPermissions = [], dataUpdatedAt } =
    useGetStaffPermissions(staffId);
  const assignPermission = useAssignPermission();
  const removePermission = useRemovePermission();

  const form = useForm({
    resolver: zodResolver(userFormSchema),
    defaultValues: toFormValues(staff),
  });

  // Reopening for a different member has to reset the form and the photo,
  // which lives outside the resolver.
  useEffect(() => {
    if (!isOpen) return;

    form.reset(toFormValues(staff));
    setProfilePic(staff?.profilePic ?? null);
    if (!staffId) setActivePermissionIds([]);
    // Keyed on which member is open, not on the object: re-running this on a
    // new `staff` reference would discard the user's edits mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, staffId]);

  // Rows cached from a previous open would be stale, so refetch and take the
  // selection from whatever comes back.
  useEffect(() => {
    if (isOpen && staffId) {
      queryClient.invalidateQueries({ queryKey: ["staffPermissions"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, staffId]);

  useEffect(() => {
    if (staffId && dataUpdatedAt) {
      setActivePermissionIds(staffPermissions.map((row) => row.permissionId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId, dataUpdatedAt]);

  const togglePermission = (permissionId) =>
    setActivePermissionIds((previous) =>
      previous.includes(permissionId)
        ? previous.filter((id) => id !== permissionId)
        : [...previous, permissionId]
    );

  const savePermissions = async (savedStaffId) => {
    const storedIds = staffPermissions.map((row) => row.permissionId);
    const toAdd = activePermissionIds.filter((id) => !storedIds.includes(id));
    const toRemove = staffPermissions.filter(
      (row) => !activePermissionIds.includes(row.permissionId)
    );

    await Promise.all([
      ...toAdd.map((permissionId) =>
        assignPermission.mutateAsync({
          staffMemberId: savedStaffId,
          permissionId,
        })
      ),
      ...toRemove.map((row) =>
        removePermission.mutateAsync({
          id: row.id,
          staffMemberId: row.staffMemberId,
          permissionId: row.permissionId,
        })
      ),
    ]);
  };

  const handleSubmit = async (values) => {
    setIsSaving(true);
    try {
      // When the tenant is at its licence limit, onSave (createNewUser) surfaces the
      // self-serve licence modal on top of this one and doesn't settle until that flow
      // finishes — resolving with the actually-saved user so permissions below still
      // apply to it, or rejecting if the admin cancels instead of adding a licence.
      // Middle names and the doctor's own qualification are not on this form. Leaving them out
      // keeps what is stored; sending "" would clear them (KIN-1724, KIN-2133).
      const saved = await onSave({ ...values, profilePic });

      const savedStaffId = saved?.id || staffId;
      if (savedStaffId) {
        try {
          await savePermissions(savedStaffId);
        } catch (error) {
          console.error("Failed to save permissions:", error);
          toast.error("The user was saved but their permissions were not");
        }
      }

      onClose();
    } catch (error) {
      // Licence flow was cancelled, or onSave otherwise failed — leave the form open
      // (with the entered values intact) so the admin can retry rather than losing them.
      console.error("User save did not complete:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={staffId ? "Edit User" : "Add New User"}
      width={1040}
      bodyClassName={styles.dialogBody}
    >
      <FormProvider {...form}>
        <form className={styles.form} onSubmit={form.handleSubmit(handleSubmit, onInvalidSubmit("UserFormModal"))}>
          <div className={styles.top}>
            <UserPhotoField value={profilePic} onChange={setProfilePic} />

            <div className={styles.grid}>
              <ModalField name="title" label="Title">
                <Select
                  options={TITLE_OPTIONS}
                  value={form.watch("title")}
                  onChange={(value) => form.setValue("title", value)}
                  fullWidth
                />
              </ModalField>

              <ModalField
                name="firstName"
                label="First name"
                htmlFor="user-first-name"
              >
                <input
                  id="user-first-name"
                  className={styles.input}
                  placeholder="First name"
                  {...form.register("firstName")}
                />
              </ModalField>

              <ModalField
                name="lastName"
                label="Last name"
                htmlFor="user-last-name"
              >
                <input
                  id="user-last-name"
                  className={styles.input}
                  placeholder="Last name"
                  {...form.register("lastName")}
                />
              </ModalField>

              <ModalField name="gender" label="Gender">
                <Select
                  options={GENDER_OPTIONS}
                  value={form.watch("gender")}
                  onChange={(value) => form.setValue("gender", value)}
                  placeholder="Select gender"
                  fullWidth
                />
              </ModalField>

              <ModalField name="mainRole" label="Role">
                <Select
                  options={ROLE_OPTIONS}
                  value={form.watch("mainRole")}
                  onChange={(value) => form.setValue("mainRole", value)}
                  fullWidth
                />
              </ModalField>

              <ModalField name="staffRole" label="Staff role">
                <Select
                  options={STAFF_ROLE_OPTIONS}
                  value={form.watch("staffRole")}
                  onChange={(value) => form.setValue("staffRole", value)}
                  placeholder="Select staff role"
                  fullWidth
                />
              </ModalField>

              <ModalField
                name="specialism"
                label="Specialisation"
                htmlFor="user-specialism"
              >
                <input
                  id="user-specialism"
                  className={styles.input}
                  {...form.register("specialism")}
                />
              </ModalField>

              <ModalField
                name="qualificationLetters"
                label="Qualifications"
                htmlFor="user-qualifications"
              >
                <input
                  id="user-qualifications"
                  className={styles.input}
                  {...form.register("qualificationLetters")}
                />
              </ModalField>

              <ModalField
                name="professionalMedicalNumber"
                label="Medical Council No."
                htmlFor="user-medical-number"
              >
                <input
                  id="user-medical-number"
                  className={styles.input}
                  placeholder="Add number"
                  {...form.register("professionalMedicalNumber")}
                />
                <p className={styles.fieldHint}>
                  Doctors only. Nurses: use the NMBI number field.
                </p>
              </ModalField>

              <ModalField
                name="nmbiNumber"
                label="NMBI No."
                htmlFor="user-nmbi-number"
              >
                <input
                  id="user-nmbi-number"
                  className={styles.input}
                  placeholder="Add number"
                  {...form.register("nmbiNumber")}
                />
                <p className={styles.fieldHint}>Nurses and midwives only.</p>
              </ModalField>

              <ModalField
                name="gmsNumber"
                label="GMS (contractor) number, 5 digits"
                htmlFor="user-gms-number"
              >
                <input
                  id="user-gms-number"
                  className={styles.input}
                  placeholder="Add number"
                  {...form.register("gmsNumber")}
                />
                <p className={styles.fieldHint}>Not the Medical Council Number.</p>
              </ModalField>

              <ModalField
                name="deaspPanelNumber"
                label="DEASP No."
                htmlFor="user-deasp-number"
              >
                <input
                  id="user-deasp-number"
                  className={styles.input}
                  placeholder="Add number"
                  {...form.register("deaspPanelNumber")}
                />
              </ModalField>

              <ModalField name="email" label="Email" htmlFor="user-email">
                <input
                  id="user-email"
                  className={styles.input}
                  type="email"
                  {...form.register("email")}
                />
              </ModalField>

              <ModalField name="mobile" label="Mobile" htmlFor="user-mobile">
                <input
                  id="user-mobile"
                  className={styles.input}
                  placeholder="Mobile number"
                  {...form.register("mobile")}
                />
              </ModalField>

              <ModalField name="homeNumber" label="Home number" htmlFor="user-home">
                <input
                  id="user-home"
                  className={styles.input}
                  {...form.register("homeNumber")}
                />
              </ModalField>
            </div>
          </div>

          <ModalField name="bio" label="Bio" htmlFor="user-bio">
            <textarea
              id="user-bio"
              className={styles.textarea}
              rows={2}
              {...form.register("bio")}
            />
          </ModalField>

          <div className={styles.columns}>
            <div className={styles.block}>
              <h4 className={styles.blockTitle}>Access</h4>
              <div className={styles.toggleList}>
                {ACCESS_TOGGLES.map((toggle) => (
                  <label key={toggle.name} className={styles.toggleRow}>
                    <Checkbox
                      checked={!!form.watch(toggle.name)}
                      handleChange={(event) =>
                        form.setValue(toggle.name, event.target.checked)
                      }
                    />
                    <span>{toggle.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.block}>
              <h4 className={styles.blockTitle}>Permissions</h4>
              {allPermissions.length === 0 ? (
                <p className={styles.blockEmpty}>
                  No permissions are configured for this practice.
                </p>
              ) : (
                <div className={styles.permissionGrid}>
                  {allPermissions.map((permission) => (
                    <label
                      key={permission.id}
                      className={styles.toggleRow}
                      htmlFor={`permission-${permission.id}`}
                    >
                      <Checkbox
                        id={`permission-${permission.id}`}
                        checked={activePermissionIds.includes(permission.id)}
                        handleChange={() => togglePermission(permission.id)}
                      />
                      <span>{permission.listName}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
